// features/articles/hooks/useArticles.ts
import { useState, useEffect } from 'react'
import { fetchArticlesByCategory } from '../api'
import type { Article, ArticleCategory } from '../types'

// Module-level cache to prevent blank screens/flickers on back navigation
const globalArticlesCache: Record<string, Article[]> = {}

const getCachedArticlesPage = (category: string): number => {
  try {
    const saved = sessionStorage.getItem(`page_cache_articles_${category}`)
    return saved ? parseInt(saved, 10) : 1
  } catch (e) {
    return 1
  }
}

const setCachedArticlesPage = (category: string, page: number) => {
  try {
    sessionStorage.setItem(`page_cache_articles_${category}`, page.toString())
  } catch (e) {
    // ignore
  }
}

export const useArticles = (category: ArticleCategory, initialPages: number = 5) => {
  const cacheKey = category

  const [articles, setArticles] = useState<Article[]>(() => {
    return globalArticlesCache[cacheKey] || []
  })
  
  const [currentPage, setCurrentPage] = useState<number>(() => {
    return getCachedArticlesPage(cacheKey)
  })
  
  const [loading, setLoading] = useState<boolean>(() => {
    return !globalArticlesCache[cacheKey]
  })
  
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const loadArticles = async () => {
      if (!globalArticlesCache[cacheKey]) {
        setLoading(true)
      }
      setError(null)

      const res = await fetchArticlesByCategory(category, initialPages)

      if (!isMounted) return

      if (res.success && res.data.length > 0) {
        setArticles(res.data)
        globalArticlesCache[cacheKey] = res.data
      } else {
        setError(res.message || 'Không thể tải bài viết')
      }
      setLoading(false)
    }

    loadArticles()

    return () => {
      isMounted = false
    }
  }, [category, initialPages, cacheKey])

  const changePage = (page: number) => {
    setCurrentPage(page)
    setCachedArticlesPage(cacheKey, page)
  }

  return {
    articles,
    loading,
    error,
    currentPage,
    changePage,
  }
}
